export type PortfolioCategoryId = "web" | "ugc" | "automation" | "video";

export interface PortfolioCategory {
  id: PortfolioCategoryId;
  label: string;
  /** Short line shown under the tabs */
  blurb: string;
  /** Message shown when a category has no verified projects yet */
  emptyTitle: string;
  emptyText: string;
}

export interface PortfolioProject {
  id: string;
  category: PortfolioCategoryId;
  /** e.g. Business Website, Landing Page, Education Website */
  subtype?: string;
  title: string;
  description: string;
  /** Path inside /public. Leave out when there is no genuine preview. */
  image?: string;
  imageAlt?: string;
  liveUrl: string;
  liveLabel: string;
  /** Small note shown on the card, e.g. site status */
  note?: string;
  /** Optional extra links. Only add when they are real. */
  altLink?: { label: string; url: string };
  caseStudyUrl?: string;
  videoUrl?: string;
}

export const portfolioCategories: PortfolioCategory[] = [
  {
    id: "web",
    label: "Web Development",
    blurb: "Business, corporate, education and campaign websites built by our team.",
    emptyTitle: "Projects coming soon",
    emptyText: "New website projects will be added here.",
  },
  {
    id: "ugc",
    label: "AI UGC",
    blurb: "Short-form AI UGC video ads for e-commerce and DTC brands.",
    emptyTitle: "Projects coming soon",
    emptyText: "New AI UGC work will be added here.",
  },
  {
    id: "automation",
    label: "AI Automation",
    blurb: "Chatbots and workflow automation for small businesses.",
    emptyTitle: "AI Automation projects are being documented.",
    emptyText: "Want to see what we can build for your business? Book a free call.",
  },
  {
    id: "video",
    label: "Video Editing",
    blurb: "Reels, ads and creative cuts for brands and creators.",
    emptyTitle: "More projects coming soon",
    emptyText: "More video editing work will be added here.",
  },
];

export const portfolioProjects: PortfolioProject[] = [
  // ---------- Web Development ----------
  {
    id: "rise-up-schools",
    category: "web",
    subtype: "Education Website",
    title: "Rise Up Schools",
    description:
      "Education website for Rise Up Schools at riseupschools.com. The full site is being built; the domain currently shows a launch page with a contact form.",
    liveUrl: "https://riseupschools.com/",
    liveLabel: "Visit Launch Page",
    note: "Website in Development",
  },
  {
    id: "skipper-ventures",
    category: "web",
    subtype: "Business Website",
    title: "Skipper Ventures",
    description:
      "Website for an Amazon growth and marketplace agency, covering PPC, SEO, creative and account management, with a free consultation call to action.",
    image: "/portfolio/skipper-ventures.webp",
    imageAlt: "Skipper Ventures website home page",
    liveUrl: "https://skipperventures.com/",
    liveLabel: "View Live",
  },
  {
    id: "skipper-venture",
    category: "web",
    subtype: "Business Website",
    title: "Skipper Venture",
    description:
      "Website for Skipper Venture LTD, an Amazon marketing agency offering PPC, SEO and account management for brands selling on Amazon.",
    image: "/portfolio/skipper-venture.webp",
    imageAlt: "Skipper Venture website home page",
    liveUrl: "https://skipperventure.com/",
    liveLabel: "View Live",
  },
  {
    id: "ecom-skipper",
    category: "web",
    subtype: "Corporate Website",
    title: "Ecom Skipper",
    description:
      "Corporate website for an e-commerce services agency, covering store setup, digital marketing, website development, AI customer support and business formation.",
    image: "/portfolio/ecom-skipper.webp",
    imageAlt: "Ecom Skipper website home page",
    liveUrl: "https://ecomskipper.com/",
    liveLabel: "View Live",
  },
  {
    id: "samu-traders",
    category: "web",
    subtype: "Business Website",
    title: "SAMU Traders",
    description:
      "Website for a sourcing and logistics company offering product sourcing from China, freight, 3PL and brand protection services.",
    image: "/portfolio/samu-traders.webp",
    imageAlt: "SAMU Traders website home page",
    liveUrl: "https://www.samutraders.com/",
    liveLabel: "View Live",
  },
  {
    id: "ecom-skipper-workshop",
    category: "web",
    subtype: "Landing Page / Campaign Page",
    title: "Ecom Skipper AI Automation Workshop",
    description:
      "Registration landing page for a free one-day AI automation workshop on 7 June 2026, with a countdown timer and a seat claim button.",
    image: "/portfolio/ecom-workshop.webp",
    imageAlt: "Ecom Skipper free AI automation workshop landing page",
    liveUrl: "https://ecomskipper.com/free-workshop-ai-automation-07-june-2026/",
    liveLabel: "View Live",
  },

  // ---------- AI UGC ----------
  {
    id: "xentic-ai-ugc",
    category: "ugc",
    subtype: "Portfolio Website",
    title: "Xentic AI UGC Portfolio",
    description:
      "Portfolio of our AI UGC studio: short-form video ads for DTC and e-commerce brands, shown as reels across different product categories.",
    image: "/portfolio/ai-ugc.webp",
    imageAlt: "Xentic AI UGC portfolio home page",
    liveUrl: "https://ai-ugc-portfolio.xenticsolutions.com/",
    liveLabel: "View Portfolio",
  },

  // ---------- AI Automation ----------
  // No verified projects yet. Add real entries here when approved.

  // ---------- Video Editing ----------
  {
    id: "xentic-video-editing",
    category: "video",
    subtype: "Portfolio Website",
    title: "Xentic Video Editing Portfolio",
    description:
      "Portfolio of our video editing work: reels, ads and creative cuts for e-commerce brands, YouTube creators and agencies.",
    image: "/portfolio/video-editing.webp",
    imageAlt: "Xentic video editing portfolio home page",
    liveUrl: "https://video-editing.xenticsolutions.com/",
    liveLabel: "View Portfolio",
  },
];

export const isPortfolioCategory = (value: string | null): value is PortfolioCategoryId =>
  portfolioCategories.some((c) => c.id === value);
